import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BrandliveEvent, LanguagesAbbr } from '../types/working-model';
import { setStorageItem } from './local-storage';
import { useLanguageParam } from '@/components/live-event/utils';
import { useIsAdmin, useIsPlatformIframe } from '@/hooks/context.hooks';

// Covers both `:language` and `:lang` route param conventions, plus any other params on the route.
type LanguageRouteParams = {
	language?: string;
	lang?: string;
	[key: string]: string | undefined;
};

function useChangeLanguage(event: BrandliveEvent | null | undefined) {
	const isAdmin = useIsAdmin();
	const isPlatformIframe = useIsPlatformIframe();
	const isEditor = isAdmin || isPlatformIframe;
	const language = useLanguageParam();
	const location = useLocation();
	const routerNavigate = useNavigate();

	// Sync storage whenever the URL language param changes.
	// This covers manual URL bar edits and any navigation that updates the language param.
	// Skipped in editor/admin contexts so admin is not affected.
	useEffect(() => {
		if (!isEditor && event?.event && language) {
			setStorageItem(`event.${event.event}.language`, language);
		}
	}, [event?.event, isEditor, language]);

	const handleLanguageChange = (newLang: LanguagesAbbr, customNavigate?: (url: string) => void) => {
		if (!event?.event) return;
		if (!language) return;

		// language is set in storage so we can remember the users chosen language so if they enter
		// a session in another language and then go back to the home page, we can reset the language to their chosen language
		if (!isEditor) {
			setStorageItem(`event.${event.event}.language`, newLang);
		}

		if (newLang && newLang !== language) {
			// replace the language segment in the current pathname with newLang
			const updatedUrl = location.pathname.replace(`/${language}`, `/${newLang}`);
			if (customNavigate) {
				customNavigate(updatedUrl);
			} else {
				routerNavigate(updatedUrl, { replace: true });
			}
		}
	};
	return handleLanguageChange;
}

export default useChangeLanguage;
